import React from "react";

import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";

import "./CustomTooltip.css";

const LightStyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#ffffff",
    color: "#333333",
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 6,
    padding: "8px 12px",
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
    border: "1px solid #D9D9D9",
    maxWidth: 300,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#ffffff",
    "&::before": {
      border: "1px solid #D9D9D9",
    },
  },
}));

const DarkStyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#616161",
    color: "#ffffff",
    fontFamily: "Inter, sans-serif",
    fontSize: 12,
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "120%",
    letterSpacing: 0,
    borderRadius: 8,
    padding: "4px 8px",
    maxWidth: 360,
    whiteSpace: "nowrap",
    position: "relative",
    marginTop: "8px !important",
    "&::before": {
      content: '""',
      position: "absolute",
      top: -6,
      left: 8,
      width: 0,
      height: 0,
      borderLeft: "5px solid transparent",
      borderRight: "5px solid transparent",
      borderBottom: "6px solid #616161",
    },
  },
  [`& .${tooltipClasses.arrow}`]: {
    display: "none",
  },
}));

// Popper modifier that shifts the tooltip right so the fixed left:8px arrow
// sits directly above the horizontal center of the trigger element.
const darkArrowAlignModifier = {
  name: "darkArrowAlign",
  enabled: true,
  phase: "main" as const,
  requires: ["popperOffsets"],
  fn({ state }: { state: any }) {
    const referenceWidth: number = state.rects.reference.width;
    // eslint-disable-next-line no-param-reassign
    if (state.modifiersData.popperOffsets) {
      // eslint-disable-next-line no-param-reassign
      state.modifiersData.popperOffsets.x += referenceWidth / 2 - 8;
    }
  },
};

interface CustomTooltipProps extends Omit<TooltipProps, "title"> {
  title: React.ReactNode;
  children: React.ReactElement;
  variant?: "light" | "dark";
}

const CustomTooltip = ({
  title,
  children,
  placement = "top",
  arrow = true,
  variant = "light",
  PopperProps: popperProps,
  ...rest
}: CustomTooltipProps) => {
  if (variant === "dark") {
    const mergedPopperProps = {
      ...popperProps,
      modifiers: [
        darkArrowAlignModifier,
        ...((popperProps?.modifiers as any[]) ?? []),
      ],
    };
    return (
      <DarkStyledTooltip
        title={title}
        placement={placement}
        arrow={arrow}
        PopperProps={mergedPopperProps}
        {...rest}
      >
        {children}
      </DarkStyledTooltip>
    );
  }
  return (
    <LightStyledTooltip title={title} placement={placement} arrow={arrow} {...rest}>
      {children}
    </LightStyledTooltip>
  );
};

export default CustomTooltip;
